var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
// Components
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { InfoBanner } from "@ui/components/info/InfoBanner";
import { FInput } from "@ui/components/inputs/FInput";
import { FCard } from "@ui/components/card";
// Redux
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAccessPoint } from "@reducers/access-points/thunk/access-point-thunk";
// API
import { accessPointsApi, } from "@api/enpoints/access-points-api";
// Utils
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import localStorageAccessor from "@utility/local-storage-accessor";
export const RequestMaintenance = () => {
    const navigate = useNavigate();
    const { apId } = useParams();
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["common"]);
    // Redux state
    const { selectedAccessPoint, singleLoading } = useAppSelector((state) => state.accessPoint);
    // Local state - try to get cached AP first
    const [cachedAp, setCachedAp] = useState(() => localStorageAccessor.getSelectedAccessPoint());
    const [submitting, setSubmitting] = useState(false);
    const currentUser = localStorageAccessor.getUser();
    const { control, handleSubmit, setValue, formState: { errors }, } = useForm({
        defaultValues: {
            location: "",
            name: localStorageAccessor.getUserDisplayName(),
            email: localStorageAccessor.getUserEmail(),
            explanation: "",
        },
    });
    // Get the effective AP data (from Redux or cache)
    const effectiveAp = selectedAccessPoint || cachedAp;
    // Fetch AP data on mount
    useEffect(() => {
        if (apId) {
            dispatch(getAccessPoint(apId));
        }
    }, [dispatch, apId]);
    // Cache AP data when it's loaded from Redux
    useEffect(() => {
        if (selectedAccessPoint) {
            localStorageAccessor.setSelectedAccessPoint(selectedAccessPoint);
            setCachedAp(selectedAccessPoint);
        }
    }, [selectedAccessPoint]);
    // Update location when AP data is loaded
    useEffect(() => {
        if (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointName) {
            setValue("location", effectiveAp.accessPointName);
        }
    }, [effectiveAp, setValue]);
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const accessPointUID = (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointUID) || apId;
        if (!accessPointUID)
            return;
        setSubmitting(true);
        try {
            const requestData = {
                accessPointUID: accessPointUID,
                userId: (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) !== null && _a !== void 0 ? _a : "",
                email: data.email,
                explanation: data.explanation,
                location: data.location,
            };
            const response = yield accessPointsApi.requestMaintenance(requestData);
            if (response.data.isSuccess) {
                toast.success(response.data.message ||
                    t("common:toasts.maintenanceRequestSubmitted"));
                navigate(-1);
            }
            else {
                toast.error(response.data.message ||
                    t("common:toasts.failedToSubmitMaintenanceRequest"));
            }
        }
        catch (error) {
            const errorMessage = ((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
                (error === null || error === void 0 ? void 0 : error.message) ||
                t("common:toasts.failedToSubmitMaintenanceRequest");
            toast.error(errorMessage);
        }
        finally {
            setSubmitting(false);
        }
    });
    const actionButton = {
        label: submitting ? t("common:actions.sending") : t("common:actions.send"),
        onClick: handleSubmit(onSubmit),
        variant: "contained",
        size: "large",
        disabled: submitting,
    };
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.requestMaintenance"), showBackButton: true, bottomActionButton: actionButton }),
        React.createElement(PageContent, { hasBottomAction: true },
            React.createElement(Grid, { container: true, spacing: 1 },
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(InfoBanner, { title: t("common:labels.requestMaintenance"), message: `You will receive a copy of your request by e-mail at the above e-mail address.
We will contact you as soon as possible.`, variant: "alarm" })),
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(Typography, { variant: "h4", my: 0.5, color: "primary.main" }, t("common:labels.requestMaintenanceHeading")),
                    React.createElement(FCard, { title: "Location", value: (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointName) || "Loading...", variant: "stats" })),
                React.createElement(Grid, { size: { xs: 12 }, mt: 1 },
                    React.createElement(FInput, { name: "name", control: control, label: t("common:labels.yourName"), disabled: true })),
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(FInput, { name: "email", control: control, label: t("common:labels.yourEmail"), disabled: true })),
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(FInput, { name: "explanation", control: control, label: t("common:labels.explanation"), placeholder: t("common:placeholders.pleaseExplain"), multiline: true, rows: 4, rules: {
                            required: t("common:toasts.pleaseProvideExplanation"),
                            maxLength: {
                                value: 1200,
                                message: t("common:toasts.explanationMaxLength"),
                            },
                        } }))))));
};
